import { getApiClient } from './authApiClient';
import { requireEmailEventId } from '../utils/emailAction';

// Base URL for the company service
// const UNDERWRITING_SERVICE_URL = 'https://underwriting-service.dev.crediarc.cloudops.space/api';


export const listCompanySummaryData = async (tenantId, options = {}) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-summary/${tenantId}`, {
    params: options.view ? { view: options.view } : undefined,
  });
  return response.data;
};

// Server-side reporting read path (admin, tenant-scoped) — slim rows + aggregates.
export const getReportingPortfolio = async (tenantId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-summary/${tenantId}/reporting`);
  return response.data?.data || { rows: [], aggregates: null };
};

// Distributor portal: AR portfolio for one assigned client tenant (authorized by distributor
// membership on the backend). Same shape as getReportingPortfolio.
export const getDistributorClientPortfolio = async (clientId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/distributor/${clientId}/reporting`);
  return response.data?.data || { rows: [], aggregates: null };
};
// Mint a tokenized embed for a BI widget (admin).
export const createEmbedToken = async (tenantId, widget = 'portfolio-summary') => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/company-summary/${tenantId}/embed-token`, { widget });
  return response.data?.data;
};

// Scheduled BI portfolio email exports.
export const listReportSchedules = async (tenantId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/report-schedules/${tenantId}`);
  return response.data?.data || [];
};
export const createReportSchedule = async (tenantId, payload) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/report-schedules/${tenantId}`, payload);
  return response.data?.data;
};

export const sendTestReport = async (tenantId, payload) => {
  requireEmailEventId(tenantId, payload?.eventId);
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/report-schedules/test/${tenantId}`, payload);
  return response.data;
};
export const deleteReportSchedule = async (tenantId, id) => {
  const apiClient = getApiClient('underwriting');
  await apiClient.delete(`/v1/report-schedules/${tenantId}/${id}`);
};
export const sendReportScheduleNow = async (tenantId, id, { eventId } = {}) => {
  requireEmailEventId(tenantId, eventId);
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/report-schedules/${tenantId}/${id}/send-now`, eventId ? { eventId } : undefined);
  return response.data;
};

// Tenant-persistent BI report templates.
export const listReportTemplates = async (tenantId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/report-schedules/templates/${tenantId}`);
  return response.data?.data || [];
};
export const saveReportTemplate = async (tenantId, payload) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/report-schedules/templates/${tenantId}`, payload);
  return response.data?.data;
};
export const deleteReportTemplate = async (tenantId, id) => {
  const apiClient = getApiClient('underwriting');
  await apiClient.delete(`/v1/report-schedules/templates/${tenantId}/${id}`);
};

export const exportCompanySummaries = async (tenantId) => {
    const apiClient = getApiClient('underwriting');
    const response = await apiClient.get(`/v1/company-summary/${tenantId}/export`, {
        responseType: 'blob', // Important for file download
    });
    return response.data;
};

export const getCompanySummary = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-summary/${tenantId}/${companyId}`);
  return response?.data?.data;
};

export const updateCompanySummary = async (tenantId, companyId, data) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/company-summary/${tenantId}/${companyId}`, { data });
  return response.data;
};

export const overrideCompanyDecision = async (tenantId, companyId, payload) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/company-summary/${tenantId}/${companyId}/admin-decision-override`, payload);
  return response.data;
};

// Generate the AI company summary via Claude (fallback when aiSummaryText is missing).
export const generateAiSummary = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/company-summary/${tenantId}/${companyId}/generate-ai-summary`);
  return response?.data?.data?.aiSummaryText || null;
};

// Trigger a portfolio re-score (score-only monitoring refresh) for specific tenants (or all
// paid tenants if none given). Runs in the background on the server.
export const triggerPortfolioRefresh = async ({ tenants, scoreOnly = true } = {}) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/monthly-refresh', { tenants, scoreOnly });
  return response?.data;
};

// Admin self-test: request the weekly AR summary without re-underwriting. Manual
// requests use their own event identity; scheduled sends use the server's weekly period.
export const sendTestWeeklyArSummary = async ({ tenantId, testEmail, refresh = false, eventId }) => {
  requireEmailEventId(tenantId, eventId);
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/weekly-ar-summary', { tenants: [tenantId], testEmail, refresh, ...(eventId ? { eventId } : {}) });
  return response?.data;
};

export const sendTenantEmail = async ({ tenantId, tenantName, type, recipients, subject, message, eventId }) => {
  requireEmailEventId(tenantId, eventId);
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/tenant-email', {
    tenantId, tenantName, type, recipients, subject, message, ...(eventId ? { eventId } : {}),
  });
  return response.data;
};

// Rescore ONLY the tenant's AR customers (score-only), then email a before/after score-delta report.
export const rescoreArCompanies = async ({ tenantId, tenantName, notifyEmail }) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/rescore-ar-delta', { tenantId, tenantName, notifyEmail });
  return response?.data;
};

export const rescoreProspectCompanies = async ({ tenantId, tenantName, notifyEmail }) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/rescore-prospects-delta', { tenantId, tenantName, notifyEmail });
  return response?.data;
};

export const getActionJob = async (jobId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/cron/action-jobs/${encodeURIComponent(jobId)}`);
  return response?.data;
};

// Admin self-test: send a sample high-risk (ARC>=8) notification email to my own email.
export const sendTestCriticalAlert = async ({ tenantId, testEmail, companyName, eventId } = {}) => {
  if (typeof tenantId !== 'string' || !tenantId.trim()) throw new Error('A tenant ID is required for a test alert.');
  requireEmailEventId(tenantId, eventId);
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/cron/test-critical-alert', { tenantId, testEmail, companyName, ...(eventId ? { eventId } : {}) });
  return response?.data;
};

// Delete company summary
export const deleteCompanySummary = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/company-summary/${tenantId}/${companyId}`);
  return response.data;
};

export const updateRequestedLimit = async (tenantId, companyId, previousLimit, requestedLimit, recommendedLimit) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/company-summary/${tenantId}/${companyId}/requested-limit`, {
    previousLimit,
    requestedLimit,
    recommendedLimit
  });
  return response.data;
};

// Get complete Google Search data
export const getCompanyGoogleSearchData = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/google-search/${tenantId}/${companyId}/google-search`);
  return response.data;
};

// Get Knowledge Graph data
export const getCompanyKnowledgeGraph = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/google-search/${tenantId}/${companyId}/knowledge-graph`);
  return response.data;
};

// Get company news
export const getCompanyNews = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/google-search/${tenantId}/${companyId}/news`);
  return response.data;
};

// Add single news item
export const addSingleCompanyNews = async (tenantId, companyId, payload) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/google-search/${tenantId}/${companyId}/news/single`, payload);
  return response.data;
};

// Delete single news item by id
export const deleteSingleNewsById = async (id) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/google-search/news/${id}`);
  return response.data;
};

// Get organic search results
export const getCompanyOrganicResults = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/google-search/${tenantId}/${companyId}/organic-results`);
  return response.data;
};

// Get company timeline events
export const getCompanyTimelineEvents = async (tenantId, companyId, companyName, options = {}) => {
  const apiClient = getApiClient('underwriting');
  const { start_date, end_date, relevance_threshold = 0.7 } = options;
  
  const params = new URLSearchParams({
    company_name: companyName,
    ...(start_date && { start_date }),
    ...(end_date && { end_date }),
    relevance_threshold: relevance_threshold.toString()
  });
  
  const response = await apiClient.get(`/v1/google-search/${tenantId}/${companyId}/timeline-events?${params}`);
  return response.data;
};

export const getAggregatedData = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/underwriting/aggregated-data/${companyId}`);
  return response.data;
};

// Update standardized ratios
export const updateStandardizedRatios = async (companyId, ratiosData) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/underwriting/aggregated-data/${companyId}/standardized-ratios`, ratiosData);
  return response.data;
};

// Remove resolved data fields
export const removeResolvedDataFields = async (tenantId, companyId, fields) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/company-summary/${tenantId}/${companyId}/remove-resolved-fields`, {
    data: { fields }
  });
  return response.data;
};

// Alternative Data endpoints
export const getAlternativeData = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/alternative-data/${companyId}`);
  return response.data;
};

export const getFinancialData = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/financial-data/${companyId}`);
  return response.data;
};

export const createAlternativeData = async (params) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/alternative-data/create', params);
  return response.data;
};

// Delete aggregated data
export const deleteAggregatedData = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/underwriting/aggregated-data/${companyId}`);
  return response.data;
};

// Delete alternative data
export const deleteAlternativeData = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/alternative-data/${companyId}`);
  return response.data;
};

// Update alternative data
export const updateAlternativeData = async (companyId, data) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/alternative-data/${companyId}`, data);
  return response.data;
};

// Company Questions endpoints
export const getCompanyQuestions = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-questions/${tenantId}/${companyId}`);
  return response.data;
};

export const createCompanyQuestion = async (tenantId, companyId, question) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/company-questions/${tenantId}/${companyId}`, {
    question,
    answer: false,
    summary: ''
  });
  return response.data;
};

export const deleteCompanyQuestion = async (tenantId, companyId, questionId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/company-questions/${tenantId}/${companyId}/${questionId}`);
  return response.data;
};

// Answer a company-specific question on demand (right after it's added) via the LLM.
// Kicks off the answer job on the backend (returns immediately, 202). The actual answer is
// produced asynchronously — poll pollCompanyQuestionAnswer for the result.
export const answerCompanyQuestion = async (tenantId, companyId, questionId, question) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(
    `/v1/company-questions/${tenantId}/${companyId}/${questionId}/answer`,
    { question },
    { timeout: 30000 }
  );
  return response.data;
};

export const getCompanyQuestionById = async (tenantId, companyId, questionId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-questions/${tenantId}/${companyId}/${questionId}`);
  return response?.data?.data || response?.data;
};

// Poll a question until its background answer finishes (answerPending clears).
export const pollCompanyQuestionAnswer = async (
  tenantId, companyId, questionId, { intervalMs = 3000, timeoutMs = 150000 } = {}
) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    try {
      const q = await getCompanyQuestionById(tenantId, companyId, questionId);
      if (q && q.answerPending === false) return q;
    } catch (e) { /* keep polling */ }
  }
  throw new Error('answer poll timed out');
};

// Mark/unmark a company question as monitored (notify when its answer is true).
export const setCompanyQuestionMonitored = async (tenantId, companyId, questionId, monitored) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(
    `/v1/company-questions/${tenantId}/${companyId}/${questionId}`,
    { monitored }
  );
  return response.data;
};

// Generate company report (PDF)
export const generateCompanyReport = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/company-report/generate/${tenantId}/${companyId}`);
  return response.data;
};

// Get signed URL for file upload (generic method)
export const getSignedUrl = async (fileName, fileType, tenantId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/aging/signed-url`, {
    fileName,
    fileType,
    tenantId
  });
  return response.data;
};

// aging process endpoint
export const processAgingFiles = async (tenantId, agingKey, paymentKey, latestAgingDate, arThreshold) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/aging/process`, {
    tenantId,
    agingKey,
    paymentKey,
    latestAgingDate,
    arThreshold
  });
  return response.data;
};

// General Questions endpoints
export const getGeneralQuestions = async (tenantId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/general-questions/${tenantId}`);
  return response.data;
};

export const getGeneralQuestionById = async (tenantId, questionId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/general-questions/${tenantId}/${questionId}`);
  return response.data;
};

export const createGeneralQuestion = async (tenantId, question) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/general-questions/${tenantId}`, { question });
  return response.data;
};

export const updateGeneralQuestion = async (tenantId, questionId, updateData) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/general-questions/${tenantId}/${questionId}`, updateData);
  return response.data;
};

export const deleteGeneralQuestion = async (tenantId, questionId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/general-questions/${tenantId}/${questionId}`);
  return response.data;
};

export const reorderGeneralQuestions = async (tenantId, questionIds) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.patch(`/v1/general-questions/${tenantId}/reorder`, { questionIds });
  return response.data;
};

export const processFile = async (fileId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/chat-files/process-file/${fileId}`);
  return response.data;
};

// Additional Files endpoints
export const getAdditionalFiles = async (companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/chat-files/companies/${companyId}/additional-files`);
  return response.data;
};

export const createAdditionalFile = async (companyId, fileData) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/chat-files/companies/${companyId}/additional-files`, fileData);
  return response.data;
};

export const deleteAdditionalFile = async (fileId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/chat-files/additional-files/${fileId}`);
  return response.data;
};

// Get eligible approvers
export const getEligibleApprovers = async (payload) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post('/v1/underwriting/eligible-approvers', payload);
  return response.data;
};

export const addNote = async (tenantId, companyId, content) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/notes/${tenantId}/${companyId}/notes`, { content });
  return response.data;
};

export const deleteNote = async (tenantId, companyId, noteId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.delete(`/v1/notes/${tenantId}/${companyId}/notes/${noteId}`);
  return response.data;
};

export const updateNote = async (tenantId, companyId, noteId, content) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put(`/v1/notes/${tenantId}/${companyId}/notes/${noteId}`, { content });
  return response.data;
};

export const getCompanyLogs = async (companyId) => {
    const apiClient = getApiClient('underwriting');
    try {
      const response = await apiClient.get(`/v1/company-logs/${companyId}/logs`);
      return response.data;
    } catch (error) { 
      console.error('Error fetching company logs:', error);
      throw error;
    }
  };

// Financial Reports  
export const getFinancialReport = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(`/v1/financial-reports/${tenantId}/${companyId}`);
  return response.data;
};

// Read-only eligibility check; report retrieval requires a separate explicit action.
export const getDnbFinancialEligibility = async (tenantId, companyId) => {
  if (typeof tenantId !== 'string' || !tenantId.trim()
      || typeof companyId !== 'string' || !companyId.trim()) {
    throw new Error('Tenant and company IDs are required to check financial-statement eligibility.');
  }

  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get(
    `/v1/financial-reports/${encodeURIComponent(tenantId)}/${encodeURIComponent(companyId)}/dnb-eligibility`
  );
  return response.data;
};

export const generateFinancialReport = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/financial-reports/${tenantId}/${companyId}/generate`);
  return response.data;
};

export const importDnbFinancialStatements = async (tenantId, companyId) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.post(`/v1/financial-reports/${tenantId}/${companyId}/import-dnb`);
  return response.data;
};

// Admin: global question configuration (text, on/off, and scoring weights).
export const getAdminQuestions = async () => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.get('/v1/underwriting/admin/questions');
  return response?.data?.data;
};

export const updateAdminQuestions = async (config) => {
  const apiClient = getApiClient('underwriting');
  const response = await apiClient.put('/v1/underwriting/admin/questions', { data: config });
  return response?.data?.data;
};

// Default export
const underwritingApi = {
  listCompanySummaryData,
  exportCompanySummaries,
  getAdminQuestions,
  updateAdminQuestions,
  getCompanySummary,
  updateCompanySummary,
  overrideCompanyDecision,
  deleteCompanySummary,
  updateRequestedLimit,
  // New Google Search API functions
  getCompanyGoogleSearchData,
  getCompanyKnowledgeGraph,
  getCompanyNews,
  getCompanyOrganicResults,
  addSingleCompanyNews,
  deleteSingleNewsById,
  // New timeline events API function
  getCompanyTimelineEvents,
  getAggregatedData,
  updateStandardizedRatios,
  deleteAggregatedData,
  removeResolvedDataFields,
  // Alternative Data functions
  getAlternativeData,
  createAlternativeData,
  deleteAlternativeData,
  updateAlternativeData,
  // Company Questions functions
  getCompanyQuestions,
  createCompanyQuestion,
  deleteCompanyQuestion,
  generateCompanyReport,
  // Aging endpoints
  getSignedUrl,
  processAgingFiles,
  // General Questions functions
  getGeneralQuestions,
  getGeneralQuestionById,
  createGeneralQuestion,
  updateGeneralQuestion,
  deleteGeneralQuestion,
  reorderGeneralQuestions,
  // Chat Files functions
  processFile,
  // Additional Files functions
  getAdditionalFiles,
  createAdditionalFile,
  deleteAdditionalFile,
  // Eligible Approvers
  getEligibleApprovers,
  // Company logs
  getCompanyLogs,
  getFinancialReport,
  getDnbFinancialEligibility,
  generateFinancialReport
};
export default underwritingApi;
